import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Autocomplete } from '@/components/common/inputs/autocomplete';
import { pickerLabels } from '@/components/common/inputs/pickerLabels';
import {
  useCreateVDM,
  useVDMCompanies,
  useCompanyHierarchy,
} from '@/api/rpc/hooks/ship-and-debit';
import {
  AS400_VDM_GROSS_AMOUNT_LIMIT,
  type VDMSupplierCompany,
} from '@erp/rpc';
import { T, useGT } from 'gt-react';

interface CreateVDMFormDefaults {
  supplierVendorNumber?: string;
  companyCode?: string;
  divisionCode?: string;
  branchCode?: string;
  grossAmount?: string;
  shippingProvince?: string;
  specialInstructions?: string;
}

interface CreateVDMModalProps {
  open: boolean;
  onClose: () => void;
  onCreated?: (vdm: { id: string }) => void;
  defaultValues?: CreateVDMFormDefaults;
  title?: string;
  submitLabel?: string;
}

interface SimpleOption {
  value: string;
  label: string;
}

const INITIAL_FORM_STATE = {
  supplierVendorNumber: '',
  companyCode: '',
  divisionCode: '',
  branchCode: '',
  calculateTax: false,
  shippingProvince: '',
  grossAmount: '',
  specialInstructions: '',
  emailCcAddress: '',
  emailBccAddress: '',
};

const getOptionValue = (o: SimpleOption) => o.value;
const getOptionLabel = (o: SimpleOption) => o.label;

/**
 * Build options list with a custom entry injected when search text
 * doesn't match any existing option.
 */
function withCustomOption(
  options: SimpleOption[],
  search: string,
): SimpleOption[] {
  const trimmed = search.trim();
  if (!trimmed) return options;
  const exists = options.some(
    (o) => o.value.toLowerCase() === trimmed.toLowerCase(),
  );
  if (exists) return options;
  return [...options, { value: trimmed, label: `${trimmed} (custom)` }];
}

export function CreateVDMModal({
  open,
  onClose,
  onCreated,
  defaultValues,
  title,
  submitLabel,
}: CreateVDMModalProps) {
  const gt = useGT();
  const [form, setForm] = useState(() =>
    defaultValues
      ? { ...INITIAL_FORM_STATE, ...defaultValues }
      : INITIAL_FORM_STATE,
  );

  useEffect(() => {
    if (open) {
      setForm(
        defaultValues
          ? { ...INITIAL_FORM_STATE, ...defaultValues }
          : INITIAL_FORM_STATE,
      );
    }
  }, [open, defaultValues]);
  const [supplierSearch, setSupplierSearch] = useState('');
  const [companySearch, setCompanySearch] = useState('');
  const [divisionSearch, setDivisionSearch] = useState('');
  const [branchSearch, setBranchSearch] = useState('');
  const createVDM = useCreateVDM();
  const submittingRef = useRef(false);
  const { companies } = useVDMCompanies();
  const { companies: companyHierarchy } = useCompanyHierarchy();

  // --- Supplier options (with custom value support) ---
  const supplierOptions = useMemo(() => {
    // Dedupe by vendorNumber to prevent React key collisions when the same
    // vendor number appears under multiple company records in the DB.
    const seenVendorNumbers = new Set<string>();
    const base: VDMSupplierCompany[] = companies.filter((c) => {
      if (seenVendorNumbers.has(c.vendorNumber)) return false;
      seenVendorNumbers.add(c.vendorNumber);
      return true;
    });

    const trimmed = supplierSearch.trim();
    if (
      trimmed &&
      !companies.some(
        (c) => c.vendorNumber.toLowerCase() === trimmed.toLowerCase(),
      )
    ) {
      base.push({
        id: '',
        name: trimmed,
        vendorNumber: trimmed,
        vdmCcEmail: null,
      });
    }
    return base;
  }, [companies, supplierSearch]);

  const getSupplierValue = useCallback(
    (s: VDMSupplierCompany) => s.vendorNumber,
    [],
  );
  const getSupplierLabel = useCallback(
    (s: VDMSupplierCompany) =>
      s.id ? `${s.name} — ${s.vendorNumber}` : `${s.vendorNumber} (custom)`,
    [],
  );

  // Custom filter — alpha searches match the supplier name only; searches
  // containing a digit fall through to the vendor number too. This prevents
  // false positives like searching "abb" picking up suppliers whose vendor
  // number happens to contain those letters (e.g. "ABB123").
  const filterSupplier = useCallback(
    (s: VDMSupplierCompany, search: string) => {
      const q = search.trim().toLowerCase();
      if (!q) return true;
      if (s.name.toLowerCase().includes(q)) return true;
      if (/\d/.test(q) && s.vendorNumber.toLowerCase().includes(q)) return true;
      return false;
    },
    [],
  );

  function handleSupplierChange(vendorNumber: string) {
    const selected = companies.find((c) => c.vendorNumber === vendorNumber);
    setForm((f) => ({
      ...f,
      supplierVendorNumber: vendorNumber,
      emailCcAddress:
        selected?.vdmCcEmail && !f.emailCcAddress
          ? selected.vdmCcEmail
          : f.emailCcAddress,
    }));
  }

  // --- Company / Division / Branch options (from sd_company_locations) ---
  const companyOptions = useMemo(
    () =>
      withCustomOption(
        companyHierarchy.map((c) => ({ value: c.code, label: c.code })),
        companySearch,
      ),
    [companyHierarchy, companySearch],
  );

  const selectedCompany = useMemo(
    () => companyHierarchy.find((c) => c.code === form.companyCode),
    [companyHierarchy, form.companyCode],
  );

  const divisionOptions = useMemo(
    () =>
      withCustomOption(
        (selectedCompany?.divisions ?? []).map((d) => ({
          value: d.code,
          label: d.code,
        })),
        divisionSearch,
      ),
    [selectedCompany, divisionSearch],
  );

  const selectedDivision = useMemo(
    () => selectedCompany?.divisions.find((d) => d.code === form.divisionCode),
    [selectedCompany, form.divisionCode],
  );

  const branchOptions = useMemo(
    () =>
      withCustomOption(
        (selectedDivision?.branches ?? []).map((b) => ({
          value: b.code,
          label: `${b.code} — ${b.description}`,
        })),
        branchSearch,
      ),
    [selectedDivision, branchSearch],
  );

  function resetAndClose() {
    setForm(INITIAL_FORM_STATE);
    setSupplierSearch('');
    setCompanySearch('');
    setDivisionSearch('');
    setBranchSearch('');
    onClose();
  }

  function handleOpenChange(isOpen: boolean) {
    if (!isOpen) {
      resetAndClose();
    }
  }

  function handleCompanyChange(value: string) {
    setForm((f) => ({
      ...f,
      companyCode: value,
      divisionCode: '',
      branchCode: '',
    }));
    setDivisionSearch('');
    setBranchSearch('');
  }

  function handleDivisionChange(value: string) {
    setForm((f) => ({ ...f, divisionCode: value, branchCode: '' }));
    setBranchSearch('');
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (submittingRef.current) return;

    if (
      !form.supplierVendorNumber.trim() ||
      !form.companyCode ||
      !form.divisionCode ||
      !form.branchCode ||
      !form.shippingProvince ||
      !form.grossAmount
    )
      return;

    submittingRef.current = true;
    try {
      const lines = form.specialInstructions
        .split('\n')
        .map((l) => l.trim().slice(0, 30));

      const vdm = await createVDM.mutateAsync({
        supplierVendorNumber: form.supplierVendorNumber.trim(),
        companyCode: form.companyCode,
        divisionCode: form.divisionCode,
        branchCode: form.branchCode,
        calculateTax: form.calculateTax,
        shippingProvince: form.shippingProvince,
        grossAmount: form.grossAmount,
        specialInstructions1: lines[0] || undefined,
        specialInstructions2: lines[1] || undefined,
        specialInstructions3: lines[2] || undefined,
        emailCcAddress: form.emailCcAddress || undefined,
        emailBccAddress: form.emailBccAddress || undefined,
      });

      onCreated?.(vdm);
      resetAndClose();
    } finally {
      submittingRef.current = false;
    }
  }

  const grossAmountOverAs400Limit =
    Number(form.grossAmount) >= AS400_VDM_GROSS_AMOUNT_LIMIT;

  const isValid =
    !grossAmountOverAs400Limit &&
    form.supplierVendorNumber.trim().length > 0 &&
    form.companyCode.length > 0 &&
    form.divisionCode.length > 0 &&
    form.branchCode.length > 0 &&
    form.shippingProvince.trim().length > 0 &&
    /^-?\d+(\.\d{1,4})?$/.test(form.grossAmount);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {title ?? (
              <T context="ship and debit create VDM modal title">
                Create Vendor Debit Memo
              </T>
            )}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto pr-4">
            <div className="space-y-4 py-2">
              {/* Supplier / Vendor Number */}
              <div className="space-y-2">
                <Label>
                  <T context="create VDM supplier field label">Supplier *</T>
                </Label>
                <Autocomplete
                  items={supplierOptions}
                  value={form.supplierVendorNumber || null}
                  onValueChange={(next) => handleSupplierChange(next ?? '')}
                  search={supplierSearch}
                  onSearchChange={setSupplierSearch}
                  getValue={getSupplierValue}
                  getLabel={getSupplierLabel}
                  filter={filterSupplier}
                  labels={pickerLabels({
                    placeholder: gt('Select or type supplier...', {
                      context: 'create VDM supplier autocomplete placeholder',
                    }),
                    search: gt('Search by name or number...', {
                      context: 'create VDM supplier search placeholder',
                    }),
                    empty: gt('Type a custom vendor number', {
                      context: 'create VDM supplier autocomplete empty message',
                    }),
                  })}
                  deselectable
                />
              </div>

              {/* Company / Division / Branch */}
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label>
                    <T context="create VDM company field label">Company *</T>
                  </Label>
                  <Autocomplete
                    items={companyOptions}
                    value={form.companyCode || null}
                    onValueChange={(next) => handleCompanyChange(next ?? '')}
                    search={companySearch}
                    onSearchChange={setCompanySearch}
                    getValue={getOptionValue}
                    getLabel={getOptionLabel}
                    labels={pickerLabels({
                      placeholder: gt('Select', {
                        context: 'create VDM company autocomplete placeholder',
                      }),
                      search: gt('Search or type...', {
                        context: 'create VDM company search placeholder',
                      }),
                      empty: gt('Type a custom code', {
                        context: 'create VDM custom code empty message',
                      }),
                    })}
                    deselectable
                  />
                </div>
                <div className="space-y-2">
                  <Label>
                    <T context="create VDM division field label">Division *</T>
                  </Label>
                  <Autocomplete
                    items={divisionOptions}
                    value={form.divisionCode || null}
                    onValueChange={(next) => handleDivisionChange(next ?? '')}
                    search={divisionSearch}
                    onSearchChange={setDivisionSearch}
                    getValue={getOptionValue}
                    getLabel={getOptionLabel}
                    labels={pickerLabels({
                      placeholder: gt('Select', {
                        context: 'create VDM division autocomplete placeholder',
                      }),
                      search: gt('Search or type...', {
                        context: 'create VDM division search placeholder',
                      }),
                      empty: gt('Type a custom code', {
                        context:
                          'create VDM custom code empty message division',
                      }),
                    })}
                    deselectable
                  />
                </div>
                <div className="space-y-2">
                  <Label>
                    <T context="create VDM branch field label">Branch *</T>
                  </Label>
                  <Autocomplete
                    items={branchOptions}
                    value={form.branchCode || null}
                    onValueChange={(value) =>
                      setForm((f) => ({ ...f, branchCode: value ?? '' }))
                    }
                    search={branchSearch}
                    onSearchChange={setBranchSearch}
                    getValue={getOptionValue}
                    getLabel={getOptionLabel}
                    labels={pickerLabels({
                      placeholder: gt('Select', {
                        context: 'create VDM branch autocomplete placeholder',
                      }),
                      search: gt('Search or type...', {
                        context: 'create VDM branch search placeholder',
                      }),
                      empty: gt('Type a custom code', {
                        context: 'create VDM custom code empty message branch',
                      }),
                    })}
                    deselectable
                  />
                </div>
              </div>

              {/* Gross Amount */}
              <div className="space-y-2">
                <Label htmlFor="grossAmount">
                  <T context="create VDM gross amount field label">
                    Gross Amount *
                  </T>
                </Label>
                <Input
                  id="grossAmount"
                  type="text"
                  inputMode="decimal"
                  placeholder={gt('0.0000', {
                    context: 'create VDM gross amount placeholder',
                  })}
                  value={form.grossAmount}
                  onChange={(e) => {
                    const val = e.target.value;
                    const dotIndex = val.indexOf('.');
                    if (dotIndex !== -1 && val.length - dotIndex - 1 > 4)
                      return;
                    setForm((f) => ({ ...f, grossAmount: val }));
                  }}
                  aria-invalid={grossAmountOverAs400Limit}
                />
                {grossAmountOverAs400Limit && (
                  <p className="text-sm text-destructive">
                    <T context="create VDM gross amount over AS400 limit error">
                      AS400 does not accept VDMs of $1,000,000 or more. Split
                      the claim across several VDMs.
                    </T>
                  </p>
                )}
              </div>

              {/* Calculate Tax + Shipping Province */}
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center gap-3 pt-6">
                  <Switch
                    id="calculateTax"
                    checked={form.calculateTax}
                    onCheckedChange={(checked) =>
                      setForm((f) => ({ ...f, calculateTax: checked }))
                    }
                  />
                  <Label htmlFor="calculateTax">
                    <T context="create VDM calculate tax switch label">
                      Calculate Tax
                    </T>
                  </Label>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="shippingProvince">
                    <T context="create VDM shipping province field label">
                      Shipping Province *
                    </T>
                  </Label>
                  <Input
                    id="shippingProvince"
                    value={form.shippingProvince}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        shippingProvince: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>

              {/* Email CC / BCC */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="emailCcAddress">
                    <T context="create VDM email CC field label">Email CC</T>
                  </Label>
                  <Input
                    id="emailCcAddress"
                    maxLength={70}
                    placeholder={gt('a@b.com,c@d.com', {
                      context: 'create VDM email list placeholder example',
                    })}
                    value={form.emailCcAddress}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        emailCcAddress: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="emailBccAddress">
                    <T context="create VDM email BCC field label">Email BCC</T>
                  </Label>
                  <Input
                    id="emailBccAddress"
                    maxLength={70}
                    placeholder={gt('a@b.com,c@d.com', {
                      context: 'create VDM email BCC placeholder example',
                    })}
                    value={form.emailBccAddress}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        emailBccAddress: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>

              {/* Special Instructions */}
              <div className="space-y-2">
                <Label htmlFor="specialInstructions">
                  <T context="create VDM special instructions field label">
                    Special Instructions
                  </T>
                </Label>
                <Textarea
                  id="specialInstructions"
                  rows={3}
                  placeholder={gt(
                    'One instruction per line (max 3 lines, 30 chars each)',
                    {
                      context: 'create VDM special instructions placeholder',
                    },
                  )}
                  value={form.specialInstructions}
                  onChange={(e) => {
                    const raw = e.target.value;
                    const wrapped: string[] = [];
                    for (const line of raw.split('\n')) {
                      let remaining = line;
                      while (remaining.length > 30 && wrapped.length < 3) {
                        wrapped.push(remaining.slice(0, 30));
                        remaining = remaining.slice(30);
                      }
                      if (wrapped.length < 3) {
                        wrapped.push(remaining.slice(0, 30));
                      }
                      if (wrapped.length >= 3) break;
                    }
                    setForm((f) => ({
                      ...f,
                      specialInstructions: wrapped.slice(0, 3).join('\n'),
                    }));
                  }}
                />
                <p className="text-xs text-muted-foreground">
                  {form.specialInstructions
                    .split('\n')
                    .slice(0, 3)
                    .map((line, i) => `Line ${i + 1}: ${line.length}/30`)
                    .join(' · ')}
                </p>
              </div>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={resetAndClose}>
              <T context="create VDM modal cancel button">Cancel</T>
            </Button>
            <Button type="submit" disabled={!isValid || createVDM.isPending}>
              {createVDM.isPending ? (
                <T context="create VDM submitting button">Creating...</T>
              ) : (
                (submitLabel ?? (
                  <T context="create VDM submit button">Create VDM</T>
                ))
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
